import Row from '../../../../../../components/Common/Row';
import TextInput from '../../../../../../kingpin/atoms/TextInput';
import Button from '../../../../../../kingpin/atoms/Button';
import Typography from '../../../../../../kingpin/atoms/Typography';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import usePopup from '../../../../../../hooks/usePopup';
import useMappedUsers from './useMappedUsers';
import _ from 'lodash';
import AppUserGridRow from './AppUserGridRow';
import Loading from '../../../../../../components/Loading';
import MappedUserItem from './MappedUserItem';
import styled from 'styled-components';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import AddMappedUserForm from './AddMappedUserForm';
import appNavItemTypeCheckers from '../../../../../../types/appNavItemTypeCheckers';
import EntityDefinitionsContext from '../../../../../../contexts/EntityDefinitionsContext';

const ListWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const List = styled.div`
  max-height: 100%;
  overflow-y: scroll;
  padding: 0px;
  padding-bottom: 64px;
`;

const useUsers = ({
  entityId,
  entityAppId,
}: {
  entityId: string;
  entityAppId: string;
}) => {
  const { mappedUsers, setMappedUsers, isLoading } = useMappedUsers({
    entityId,
    entityAppId,
  });
  const [users, setUsers] = useState<MappedUser[]>(mappedUsers);
  const [searchText, setSearchText] = useState<string>('');
  const onSearchChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  useEffect(() => {
    const baseUsers = mappedUsers.filter((u) =>
      (u.user.displayName ?? '')
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
    const sortedUsers = _.sortBy(baseUsers, 'user.displayName');
    setUsers(sortedUsers);
  }, [mappedUsers, searchText]);

  return {
    users,
    searchText,
    onSearchChanged,
    setMappedUsers,
    isLoading,
  };
};

const MappedUsersList = ({
  entityAppId,
  entity,
  entityName,
  entityLabel,
}: {
  entityAppId: string;
  entity: EntityDetails.Entity;
  entityName: string;
  entityLabel: string;
}) => {
  const { isOpen, open, close } = usePopup();
  const { users, searchText, onSearchChanged, isLoading, setMappedUsers } =
    useUsers({
      entityAppId,
      entityId: entity.id,
    });

  return (
    <>
      <Row spaceBetween centerAlign style={{ height: 64 }}>
        <div style={{ width: 300 }}>
          <TextInput
            value={searchText}
            onChange={onSearchChanged}
            icon={'search'}
            placeholder={'Search'}
          />
        </div>

        <Button
          size={'Medium'}
          type={'Primary'}
          label={`Add ${entity.name}`}
          onClick={open}
        />
      </Row>
      <AppUserGridRow
        isHeader
        NameCol={<Typography.Body type="Label">Name</Typography.Body>}
        EmailCol={<Typography.Body type="Label">Email</Typography.Body>}
        FilterValueCol={
          <Typography.Body type="Label">{entity.name}</Typography.Body>
        }
      />
      <ListWrapper>
        {isLoading && <Loading />}
        {!isLoading && (
          <List>
            {users.map((u) => (
              <MappedUserItem
                user={u}
                entity={entity}
                entityName={entityName}
                setMappedUsers={setMappedUsers}
                key={u.user.id}
                mappedUsers={users}
                entityLabel={entityLabel}
              />
            ))}
          </List>
        )}
      </ListWrapper>
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <AddMappedUserForm
              close={close}
              entity={entity}
              entityAppId={entityAppId}
              setMappedUsers={setMappedUsers}
              entityName={entityName}
              entityLabel={entityLabel}
              mappedUsers={users}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export const getMappedEntityNavItem = (
  app: V3.ProductApp,
  entityId: string,
) => {
  let item: V3.Navigation.MappedEntityView | undefined;

  app.navigationSettings.sections.forEach((section) => {
    section.items.forEach((i) => {
      if (appNavItemTypeCheckers.isMappedEntityView(i)) {
        if (i.entityId === entityId) {
          item = i;
        }
      }
    });
  });

  return item;
};

const Gate = ({
  app,
  entityId,
  entityAppId,
  entityLabel,
}: {
  app: V3.ProductApp;
  entityId: string;
  entityAppId: string;
  entityLabel: string;
}) => {
  const [entity, setEntity] = useState<EntityDetails.Entity | undefined>();
  const [entityName, setEntityName] = useState<string | undefined>();
  const { entityDefinitions } = useContext(EntityDefinitionsContext);

  useEffect(() => {
    const mappedEntityNavItem = getMappedEntityNavItem(app, entityId);

    const entityName = (() => {
      if (!mappedEntityNavItem) {
        return undefined;
      }

      return mappedEntityNavItem.usersTabLabel;
    })();
    setEntityName(entityName);
    setEntity(entityDefinitions.find((e) => e.id === entityId));
  }, [entityDefinitions, entityId, app]);

  if (!entity || !entityName) {
    return null;
  }

  return (
    <MappedUsersList
      entity={entity}
      entityName={entityName}
      entityAppId={entityAppId}
      entityLabel={entityLabel}
    />
  );
};

export default Gate;
